import { MkIcon } from "@/mk/MkIcon";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { MkButton } from "./MkButton";
import { MkModal } from "./MkModal";

export interface FilterGroup {
    id: string;
    label: string;
    options: { value: string; label: string; badge?: number }[];
}

export type FilterValue = Record<string, string[]>;

interface Props {
    open: boolean;
    title?: string;
    groups: FilterGroup[];
    value?: FilterValue;
    onApply?(value: FilterValue): void;
    onClear?(): void;
    onClose(): void;
}

function clone(value: FilterValue | undefined): FilterValue {
    return Object.fromEntries(Object.entries(value ?? {}).map(([key, list]) => [key, [...list]]));
}

export function MkFilterModal({ open, title, groups, value, onApply, onClear, onClose }: Props) {
    const { t } = useTranslation();
    const [selection, setSelection] = useState<FilterValue>(() => clone(value));
    const wasOpen = useRef(open);

    useEffect(() => {
        if (open && !wasOpen.current) {
            setSelection(clone(value));
        }
        wasOpen.current = open;
    }, [open, value]);

    const isActive = (group: string, option: string) => (selection[group] ?? []).includes(option);
    const count = Object.values(selection).reduce((total, list) => total + (list ? list.length : 0), 0);

    function toggle(group: string, option: string) {
        setSelection((current) => {
            const list = current[group] ?? [];
            return {
                ...current,
                [group]: list.includes(option) ? list.filter((item) => item !== option) : [...list, option],
            };
        });
    }

    return (
        <MkModal open={open} size="md" title={title || t("mk.filter.title")} icon="fa-sliders"
                 onClose={onClose}
                 footer={(
                     <div className="mk-filter-footer">
                         <MkButton variant="ghost" icon="fa-eraser" label={t("mk.filter.clearAll")}
                                   onClick={() => {
                                       setSelection({});
                                       onClear?.();
                                   }} />
                         <MkButton variant="success" icon="fa-check"
                                   label={count ? t("mk.filter.applyCount", { value: count })
                                                : t("mk.filter.apply")}
                                   onClick={() => onApply?.(clone(selection))} />
                     </div>
                 )}>
            <div className="mk-filter">
                {groups.filter((group) => group.options?.length).map((group) => (
                    <div className="mk-filter-group" key={group.id}>
                        <div className="mk-filter-glabel">{group.label}</div>
                        <div className="mk-filter-opts">
                            {group.options.map((option) => (
                                <button key={option.value} type="button"
                                        className={`mk-filter-opt${isActive(group.id, option.value) ? " mk-filter-opt-on" : ""}`}
                                        onClick={() => toggle(group.id, option.value)}>
                                    {isActive(group.id, option.value) && <MkIcon name="fa-check" />}
                                    <span>{option.label}</span>
                                    {option.badge !== undefined && (
                                        <span className="mk-filter-badge">{option.badge}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </MkModal>
    );
}
